import { LitElement, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { repeat } from 'lit/directives/repeat.js';
import { dispatch } from '../../utils/event-helpers';
import { generateUid } from '../../utils/uid';
import { inputStyles } from './asc-input.styles';
import '../asc-badge/asc-badge';

type InputSize = 'sm' | 'md';
type InputMode = 'default' | 'tags' | 'payment';

/** Generic credit card SVG — payment mode placeholder (all network values). */
const CARD_ICON_SVG = html`
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="1.5" y="4.5" width="17" height="11" rx="2" stroke="currentColor" stroke-width="1.5" />
    <path d="M1.5 8.5H18.5" stroke="currentColor" stroke-width="1.5" />
    <rect x="3.5" y="11" width="4" height="2" rx="0.5" fill="currentColor" />
  </svg>
`;

/** Exclamation-circle SVG for the destructive error state in the trailing position. */
const ERROR_ICON_SVG = html`
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.5" />
    <path d="M8 5V9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
    <circle cx="8" cy="11.25" r="0.75" fill="currentColor" />
  </svg>
`;

/**
 * The standard single-line text input control for the Ascensus design system.
 * Supports leading and trailing affordances (icon, dropdown, or static text prefix),
 * inline tag/chip entry, payment card input with a generic network icon, and an
 * externally attached action button. Fully controlled and presentational — tag
 * management, form validation, and dropdown state are the caller's responsibility.
 *
 * @element asc-input
 *
 * @fires asc-input           - Typed input change (default/payment). Detail: `{ value: string }`
 * @fires asc-tag-add         - New tag committed via Enter or comma (tags mode). Detail: `{ value: string }`
 * @fires asc-tag-remove      - Tag removed via chip × or Backspace on empty input (tags mode). Detail: `{ value: string }`
 * @fires asc-dropdown-change - Slotted leading/trailing dropdown changed. Detail: `{ position: 'leading' | 'trailing', value: string }`
 * @fires asc-focus           - Internal input gained focus. Detail: `{ value: string }`
 * @fires asc-blur            - Internal input lost focus. Detail: `{ value: string }`
 *
 * @slot label             - Required. Label text rendered above the input.
 * @slot hint              - Optional. Helper text below the input. Hidden when `destructive`.
 * @slot error             - Optional. Error message. Visible only when `destructive`.
 * @slot leading-icon      - Optional. Leading icon. Mutually exclusive with other leading affordances.
 * @slot leading-dropdown  - Optional. Leading dropdown (highest priority leading affordance).
 * @slot leading-text      - Optional. Leading static text prefix (second-priority).
 * @slot trailing-icon     - Optional. Trailing icon. Replaced by error icon when `destructive`. Mutually exclusive with `trailing-dropdown`.
 * @slot trailing-dropdown - Optional. Trailing dropdown (higher priority than `trailing-icon`).
 * @slot trailing-button   - Optional. Label content (text or icon) for the attached trailing action button.
 *
 * @csspart base                - Root flex-column wrapper.
 * @csspart label-row           - Wrapper around the label slot.
 * @csspart field-row           - Horizontal flex row wrapping the field and trailing-button slot.
 * @csspart field               - Styled input field container (border, bg, shadow, radius).
 * @csspart leading             - Leading affordance container.
 * @csspart leading-divider     - Vertical divider after leading dropdown/text.
 * @csspart chips               - Tag chip list (`<ul>`).
 * @csspart chip                - Individual tag chip (`<li>` wrapping `<asc-badge>`).
 * @csspart input               - The native `<input>` element.
 * @csspart trailing            - Trailing affordance container.
 * @csspart trailing-divider    - Vertical divider before trailing dropdown.
 * @csspart trailing-button      - Shadow DOM `<button>` element for the attached trailing action.
 * @csspart hint-row            - Wrapper around hint or error slot.
 *
 * @cssprop --asc-input-height-sm                - Field height at size="sm". Default: `36px`
 * @cssprop --asc-input-height-md                - Field height at size="md". Default: `44px`
 * @cssprop --asc-input-padding-x                - Horizontal padding inside the field. Default: `14px`
 * @cssprop --asc-input-padding-y-sm             - Vertical padding at size="sm". Default: `var(--asc-spacing-xs)`
 * @cssprop --asc-input-padding-y-md             - Vertical padding at size="md". Default: `var(--asc-spacing-md)`
 * @cssprop --asc-input-border-radius            - Border radius of the field. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-input-attached-button-gap      - Gap between field and trailing-button. Default: `var(--asc-spacing-xs)`
 * @cssprop --asc-input-field-gap                - Vertical gap between label, field, and hint/error. Default: `var(--asc-spacing-sm)`
 * @cssprop --asc-input-font-size-sm             - Input text font size at size="sm". Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-input-font-size-md             - Input text font size at size="md". Default: `var(--asc-font-size-text-md)`
 * @cssprop --asc-input-bg                       - Field background color. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-input-bg-disabled              - Field background when disabled. Default: `var(--asc-bg-disabled_subtle)`
 * @cssprop --asc-input-border-color             - Border color in default state. Default: `var(--asc-border-primary)`
 * @cssprop --asc-input-border-color-focus       - Border color when focused. Default: `var(--asc-border-brand)`
 * @cssprop --asc-input-border-color-destructive - Border color when destructive. Default: `var(--asc-border-error)`
 * @cssprop --asc-input-border-color-disabled    - Border color when disabled. Default: `var(--asc-border-disabled)`
 * @cssprop --asc-input-border-width             - Border thickness. Default: `var(--asc-border-width)`
 * @cssprop --asc-input-shadow                   - Field drop shadow. Default: `0px 1px 2px 0px var(--asc-shadow-xs)`
 * @cssprop --asc-input-focus-ring               - :focus-visible outline. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-input-font-family              - Font family for input text. Default: `var(--asc-font-family-body)`
 * @cssprop --asc-input-font-weight              - Font weight for input text. Default: `var(--asc-font-weight-regular)`
 * @cssprop --asc-input-text-color               - Input text color. Default: `var(--asc-text-primary)`
 * @cssprop --asc-input-text-color-placeholder   - Placeholder text color. Default: `var(--asc-text-placeholder)`
 * @cssprop --asc-input-text-color-disabled      - Text color when disabled. Default: `var(--asc-text-disabled)`
 * @cssprop --asc-input-affordance-color         - Leading/trailing icon and text color. Default: `var(--asc-fg-tertiary)`
 * @cssprop --asc-input-affordance-color-disabled - Affordance color when disabled. Default: `var(--asc-fg-disabled)`
 * @cssprop --asc-input-divider-color            - Divider color. Default: `var(--asc-border-tertiary)`
 * @cssprop --asc-input-affordance-gap           - Gap between affordance container and input text. Default: `var(--asc-spacing-md)`
 * @cssprop --asc-input-error-icon-color         - Error icon color when destructive. Default: `var(--asc-fg-error-primary)`
 * @cssprop --asc-input-label-color              - Label text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-input-label-font-size          - Label font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-input-label-font-weight        - Label font weight. Default: `var(--asc-font-weight-medium)`
 * @cssprop --asc-input-hint-color               - Hint text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-input-hint-font-size           - Hint/error font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-input-error-color              - Error message text color. Default: `var(--asc-text-error-primary)`
 * @cssprop --asc-input-chip-gap                 - Gap between chips and the inline input cursor. Default: `var(--asc-spacing-md)`
 *
 * Tag chip appearance is controlled via `<asc-badge>` CSS custom properties
 * (e.g. `--asc-badge-bg`, `--asc-badge-border-color`, `--asc-badge-fg`) inherited
 * into the shadow DOM of each badge. Chips always render with `theme="gray"`,
 * `variant="badge"`, and `size="sm"`.
 */
@customElement('asc-input')
export class AscInput extends LitElement {
  static formAssociated = true;

  static override styles = [inputStyles];

  static override shadowRootOptions: ShadowRootInit = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  private readonly _uid = generateUid();

  private readonly _internals = this.attachInternals();

  // ─── Reflected properties ──────────────────────────────────────

  /** Controls height, padding, and font size of the input. */
  @property({ type: String, reflect: true }) size: InputSize = 'md';

  /** Controls the input behavior: standard text (`default`), tag/chip entry (`tags`), or payment card (`payment`). */
  @property({ type: String, reflect: true }) mode: InputMode = 'default';

  /** Payment network identifier shown as a leading icon in payment mode (e.g. `'visa'`, `'mastercard'`). Unknown values use a generic card icon. */
  @property({ type: String, reflect: true, attribute: 'payment-network' }) paymentNetwork = '';

  /** Marks the field as required — sets `aria-required="true"` and the `required` attribute on the underlying input. */
  @property({ type: Boolean, reflect: true }) required = false;

  /** Disables the input and all affordances. Takes precedence over `destructive`. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Renders the error state: error border, error icon in trailing position, and shows the `error` slot instead of `hint`. */
  @property({ type: Boolean, reflect: true }) destructive = false;

  // ─── Non-reflected properties ──────────────────────────────────

  /** Placeholder text shown inside the input when no value or tags are present. */
  @property({ type: String }) placeholder = '';

  /** Current string value. Used for controlled binding in `default` and `payment` modes. */
  @property({ type: String }) value = '';

  /**
   * Array of current tag values (tags mode). Fully controlled — the caller updates
   * this in response to `asc-tag-add` / `asc-tag-remove` events.
   */
  @property({ type: Array }) tags: string[] = [];

  /** The `name` for form participation via `ElementInternals`. */
  @property({ type: String }) name = '';

  // ─── Internal state ────────────────────────────────────────────

  @state() private _hasLeadingDropdown = false;
  @state() private _hasLeadingText = false;
  @state() private _hasLeadingIcon = false;
  @state() private _hasTrailingDropdown = false;
  @state() private _hasTrailingIcon = false;
  @state() private _hasTrailingButton = false;

  // ─── DOM references ────────────────────────────────────────────

  @query('[part="input"]') private _inputEl!: HTMLInputElement;

  // ─── Lifecycle ─────────────────────────────────────────────────

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('value') || changed.has('name')) {
      this._internals.setFormValue(this.value);
    }
  }

  // ─── Slot-change handlers ──────────────────────────────────────

  private _handleLeadingSlotChange(): void {
    const root = this.shadowRoot!;
    const hasDropdown =
      (root.querySelector('slot[name="leading-dropdown"]') as HTMLSlotElement).assignedNodes()
        .length > 0;
    const hasText =
      (root.querySelector('slot[name="leading-text"]') as HTMLSlotElement).assignedNodes().length >
      0;
    const hasIcon =
      (root.querySelector('slot[name="leading-icon"]') as HTMLSlotElement).assignedNodes().length >
      0;
    const count = [hasDropdown, hasText, hasIcon].filter(Boolean).length;

    if (count > 1) {
      // development-only warning per spec
      console.warn(
        '[asc-input] Multiple leading affordances detected. Rendering by priority: leading-dropdown > leading-text > leading-icon.'
      );
    }

    this._hasLeadingDropdown = hasDropdown;
    this._hasLeadingText = !hasDropdown && hasText;
    this._hasLeadingIcon = !hasDropdown && !hasText && hasIcon;
  }

  private _handleTrailingSlotChange(): void {
    const root = this.shadowRoot!;
    const hasDropdown =
      (root.querySelector('slot[name="trailing-dropdown"]') as HTMLSlotElement).assignedNodes()
        .length > 0;
    const hasIcon =
      (root.querySelector('slot[name="trailing-icon"]') as HTMLSlotElement).assignedNodes()
        .length > 0;

    if (hasDropdown && hasIcon) {
      // development-only warning per spec
      console.warn(
        '[asc-input] Both trailing-dropdown and trailing-icon are set. Rendering trailing-dropdown only.'
      );
    }

    this._hasTrailingDropdown = hasDropdown;
    this._hasTrailingIcon = !hasDropdown && hasIcon;
  }

  // ─── Event handlers ────────────────────────────────────────────

  private _handleInput(): void {
    // Tags mode has no 'asc-input' contract — commits go through asc-tag-add instead.
    if (this.mode === 'tags') return;
    this.value = this._inputEl.value;
    // _internals.setFormValue is synced via updated() when this.value changes.
    dispatch<{ value: string }>(this, 'asc-input', { detail: { value: this.value } });
  }

  private _handleKeydown(e: KeyboardEvent): void {
    if (this.mode !== 'tags') return;

    if ((e.key === 'Enter' || e.key === ',') && this._inputEl.value.trim()) {
      e.preventDefault();
      const value = this._inputEl.value.trim().replace(/,$/, '');
      dispatch<{ value: string }>(this, 'asc-tag-add', { detail: { value } });
      this._inputEl.value = '';
    } else if (e.key === 'Backspace' && !this._inputEl.value && this.tags.length > 0) {
      dispatch<{ value: string }>(this, 'asc-tag-remove', {
        detail: { value: this.tags[this.tags.length - 1]! },
      });
    }
  }

  private _handleTagRemove(tag: string): void {
    dispatch<{ value: string }>(this, 'asc-tag-remove', { detail: { value: tag } });
  }

  private _handleDropdownChange(position: 'leading' | 'trailing', e: Event): void {
    e.stopPropagation();
    const target = e.target as HTMLSelectElement;
    dispatch<{ position: 'leading' | 'trailing'; value: string }>(this, 'asc-dropdown-change', {
      detail: { position, value: target.value },
    });
  }

  private _handleFieldClick(e: MouseEvent): void {
    // Don't steal focus from interactive elements (e.g. native <select> affords) in the composed path.
    // Without this guard the native select dropdown closes immediately because the input gets focused.
    const isInteractive = e.composedPath().some(
      (node) =>
        node instanceof HTMLElement &&
        node !== this &&
        (node instanceof HTMLSelectElement ||
          node instanceof HTMLInputElement ||
          node instanceof HTMLButtonElement ||
          node instanceof HTMLTextAreaElement ||
          node.isContentEditable)
    );
    if (!isInteractive) {
      this._inputEl?.focus();
    }
  }

  private _handleTrailingButtonSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasTrailingButton = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _handleFocus(): void {
    dispatch<{ value: string }>(this, 'asc-focus', { detail: { value: this.value } });
  }

  private _handleBlur(): void {
    dispatch<{ value: string }>(this, 'asc-blur', { detail: { value: this.value } });
  }

  override render() {
    const labelId = `${this._uid}-label`;
    const hintId = `${this._uid}-hint`;
    const errorId = `${this._uid}-error`;
    const describedBy = this.destructive ? errorId : hintId;

    const showLeadingPayment = this.mode === 'payment';
    // In payment mode, leading slots are ignored per spec.
    const showLeadingContainer =
      showLeadingPayment ||
      this._hasLeadingDropdown ||
      this._hasLeadingText ||
      this._hasLeadingIcon;
    const showLeadingDivider = this._hasLeadingDropdown || this._hasLeadingText;

    const showTrailingContainer =
      this._hasTrailingDropdown ||
      this._hasTrailingIcon ||
      (this.destructive && !this._hasTrailingDropdown);
    const showTrailingDivider = this._hasTrailingDropdown;

    return html`
      <div part="base" role="group" aria-labelledby=${labelId}>
        <div part="label-row" id=${labelId}>
          <slot name="label"></slot>
        </div>

        <div part="field-row" class=${this._hasTrailingButton ? 'has-trailing-button' : ''}>
          <div part="field" @click=${this._handleFieldClick}>

            <!-- Leading affordance container — always in DOM for slot detection -->
            <div part="leading" ?hidden=${!showLeadingContainer}>
              ${showLeadingPayment
                ? html`<span class="payment-icon" aria-hidden="true">${CARD_ICON_SVG}</span>`
                : nothing}
              <!-- All leading slots always present in DOM so slotchange fires on first assign -->
              <slot
                name="leading-dropdown"
                ?hidden=${showLeadingPayment || !this._hasLeadingDropdown}
                @slotchange=${this._handleLeadingSlotChange}
                @change=${(e: Event) => this._handleDropdownChange('leading', e)}
              ></slot>
              <slot
                name="leading-text"
                ?hidden=${showLeadingPayment || !this._hasLeadingText}
                @slotchange=${this._handleLeadingSlotChange}
              ></slot>
              <slot
                name="leading-icon"
                ?hidden=${showLeadingPayment || !this._hasLeadingIcon}
                @slotchange=${this._handleLeadingSlotChange}
              ></slot>
              ${showLeadingDivider
                ? html`<span part="leading-divider" aria-hidden="true"></span>`
                : nothing}
            </div>

            <!-- Tags area: chips + input wrapped together so leading/trailing stay pinned -->
            ${this.mode === 'tags'
              ? html`
                  <div class="tags-area">
                    ${this.tags.length > 0
                      ? html`
                          <ul part="chips" role="list" aria-label="Selected tags">
                            ${repeat(
                              this.tags,
                              (tag) => tag,
                              (tag) => html`
                                <li part="chip" role="listitem">
                                  <asc-badge theme="gray" variant="badge" size="sm">
                                    ${tag}
                                    <button
                                      slot="trailing"
                                      type="button"
                                      class="tag-remove-btn"
                                      aria-label="Remove ${tag}"
                                      ?disabled=${this.disabled}
                                      @click=${(e: MouseEvent) => {
                                        e.stopPropagation();
                                        this._handleTagRemove(tag);
                                      }}
                                    >
                                      <svg
                                        width="10"
                                        height="10"
                                        viewBox="0 0 10 10"
                                        fill="none"
                                        aria-hidden="true"
                                      >
                                        <path
                                          d="M1 1L9 9M9 1L1 9"
                                          stroke="currentColor"
                                          stroke-width="1.5"
                                          stroke-linecap="round"
                                        />
                                      </svg>
                                    </button>
                                  </asc-badge>
                                </li>
                              `
                            )}
                          </ul>
                        `
                      : nothing}
                    <input
                      part="input"
                      type="text"
                      placeholder=${ifDefined(this.placeholder || undefined)}
                      ?disabled=${this.disabled}
                      aria-required=${this.required ? 'true' : 'false'}
                      aria-disabled=${this.disabled ? 'true' : 'false'}
                      aria-invalid=${this.destructive ? 'true' : 'false'}
                      aria-describedby=${describedBy}
                      @input=${this._handleInput}
                      @keydown=${this._handleKeydown}
                      @focus=${this._handleFocus}
                      @blur=${this._handleBlur}
                    />
                  </div>
                `
              : html`
                  <input
                    part="input"
                    type="text"
                    .value=${live(this.value)}
                    placeholder=${ifDefined(this.placeholder || undefined)}
                    name=${ifDefined(this.name || undefined)}
                    ?required=${this.required}
                    ?disabled=${this.disabled}
                    aria-required=${this.required ? 'true' : 'false'}
                    aria-disabled=${this.disabled ? 'true' : 'false'}
                    aria-invalid=${this.destructive ? 'true' : 'false'}
                    aria-describedby=${describedBy}
                    @input=${this._handleInput}
                    @keydown=${this._handleKeydown}
                    @focus=${this._handleFocus}
                    @blur=${this._handleBlur}
                  />
                `}

            <!-- Trailing affordance container — always in DOM for slot detection -->
            <div part="trailing" ?hidden=${!showTrailingContainer}>
              ${showTrailingDivider
                ? html`<span part="trailing-divider" aria-hidden="true"></span>`
                : nothing}
              ${this.destructive && !this._hasTrailingDropdown
                ? html`<span class="error-icon" aria-hidden="true">${ERROR_ICON_SVG}</span>`
                : nothing}
              <slot
                name="trailing-dropdown"
                ?hidden=${!this._hasTrailingDropdown}
                @slotchange=${this._handleTrailingSlotChange}
                @change=${(e: Event) => this._handleDropdownChange('trailing', e)}
              ></slot>
              <slot
                name="trailing-icon"
                ?hidden=${!this._hasTrailingIcon}
                @slotchange=${this._handleTrailingSlotChange}
              ></slot>
            </div>
          </div>

          <!-- Trailing action button: shadow DOM <button> wraps slot so all states are fully styled -->
          <button
            type="button"
            part="trailing-button"
            ?hidden=${!this._hasTrailingButton}
            ?disabled=${this.disabled}
          >
            <slot
              name="trailing-button"
              @slotchange=${this._handleTrailingButtonSlotChange}
            ></slot>
          </button>
        </div>

        <!-- Hint / error row -->
        <div part="hint-row">
          <slot name="error" id=${errorId} ?hidden=${!this.destructive} role="alert"></slot>
          <slot name="hint" id=${hintId} ?hidden=${this.destructive}></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-input': AscInput;
  }
}
